import { forwardRef, type HTMLAttributes } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/cn'

export type FeatureCardVariant = 'default' | 'gold' | 'premium'

interface FeatureCardProps extends HTMLAttributes<HTMLDivElement> {
  icon: React.ElementType
  title: string
  description: string
  variant?: FeatureCardVariant
  delay?: number
  animated?: boolean
}

const variantStyles = {
  default: {
    card: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-card-hover',
    icon: 'bg-marykay-pink/20 text-marykay-pink',
    iconGlow: 'group-hover:shadow-[0_0_20px_rgba(255,107,169,0.3)]',
  },
  gold: {
    card: 'bg-gradient-to-br from-marykay-gold-soft to-white dark:from-gray-800 dark:to-gray-900 border border-marykay-gold/30 shadow-gold hover:shadow-gold-hover',
    icon: 'bg-marykay-gold/20 text-marykay-gold',
    iconGlow: 'group-hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]',
  },
  premium: {
    card: 'bg-gradient-to-br from-marykay-pink-soft to-marykay-gold-soft dark:from-gray-800 dark:to-gray-900 border border-marykay-gold/20 shadow-card hover:shadow-gold',
    icon: 'bg-gradient-to-br from-marykay-pink to-marykay-gold text-white',
    iconGlow: 'group-hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]',
  },
}

export const FeatureCard = forwardRef<HTMLDivElement, FeatureCardProps>(
  (
    {
      icon: Icon,
      title,
      description,
      variant = 'default',
      delay = 0,
      animated = true,
      className,
      ...props
    },
    ref
  ) => {
    const styles = variantStyles[variant]

    const content = (
      <div
        ref={!animated ? ref : undefined}
        className={cn(
          'group rounded-card p-6 transition-all duration-300',
          styles.card,
          className
        )}
        {...props}
      >
        <div
          className={cn(
            'w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-shadow duration-300',
            styles.icon,
            styles.iconGlow
          )}
        >
          <Icon className="w-7 h-7" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          {description}
        </p>
      </div>
    )

    if (animated) {
      return (
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5, delay }}
          whileHover={{ scale: 1.02, y: -4 }}
        >
          {content}
        </motion.div>
      )
    }

    return content
  }
)

FeatureCard.displayName = 'FeatureCard'

export default FeatureCard
